import { useCallback, useEffect, useRef, useState } from 'react'
import {
  autoAssignMatch,
  deleteMatch,
  fetchMatches,
  fetchParseStatus,
  fetchUpcoming,
  fetchEsplayFixtures,
  uploadDemo,
  fetchBriefs,
  sendBrief,
  setFixtureForfeit,
  type ForfeitResult,
  type PreMatchBrief,
  fetchDemoGaps,
  addDemoFromUrl,
  type FixtureGap,
  type MatchListItem,
  type NextMatch,
} from '../data/api'
import { SectionLabel } from './RoundTimeline'
import { useCanCoach } from '../data/session'
import { fmtDate, fmtFixture } from '../data/fmt'

type UploadState =
  | { s: 'idle' }
  | { s: 'uploading'; pct: number }
  | { s: 'queued'; demoId: string }
  | { s: 'error'; msg: string }

export function Home({ onSelect, onOpponent }: { onSelect: (id: string) => void; onOpponent?: (id: string) => void }) {
  const [matches, setMatches] = useState<MatchListItem[]>([])
  const [next, setNext] = useState<NextMatch[]>([])
  const [loaded, setLoaded] = useState(false)
  // Tracked separately from `loaded` because the two arrive at different times and the gap is what
  // used to make this page feel broken: the match list paints first, and the fixture sections then
  // appeared ABOVE it and shoved it down — while the coach was reading it. Knowing the fixtures are
  // still coming lets the space be held instead of inserted.
  const [nextLoaded, setNextLoaded] = useState(false)
  const [up, setUp] = useState<UploadState>({ s: 'idle' })
  // A season is ~25 demos and grows; a flat list stops being findable somewhere around ten.
  const [fMap, setFMap] = useState('')
  const [fLeague, setFLeague] = useState('')
  const [fOpp, setFOpp] = useState('')
  const [showBrief, setShowBrief] = useState(false)

  const oppOf = (m: MatchListItem): string =>
    Object.keys(m.teamScore ?? {}).find((t) => !/jano/i.test(t)) ?? ''
  const maps = [...new Set(matches.map((m) => m.map).filter(Boolean))].sort()
  const leagues = [...new Set(matches.map((m) => m.league).filter(Boolean))].sort() as string[]
  const shown = matches.filter(
    (m) =>
      (!fMap || m.map === fMap) &&
      (!fLeague || m.league === fLeague) &&
      (!fOpp || oppOf(m).toLowerCase().includes(fOpp.toLowerCase())),
  )
  // Uploading is everyone's job; deleting a match takes the demo and its stats with it.
  const canCoach = useCanCoach()
  // "Next up" means what it says: fixtures still ahead of us. A match that has been played, or
  // settled as a walkover that never will be, belongs to the list of things still owed instead —
  // the demo it is missing, or the forfeit mark that can still be undone.
  const upcoming = next.filter((m) => !m.played && !m.forfeit)
  // "Loose ends" is a to-do list — it holds fixtures that still owe something. A played esplay
  // fixture owes nothing: its demo can't be fetched without a signed-in esplay session, so it would
  // sit here forever wearing a badge nobody can clear. Once played, a Nuke-liiga game is history,
  // and the season fold is where history lives.
  const settled = next.filter((m) => (m.played || m.forfeit) && m.source !== 'esplay')
  const [elapsed, setElapsed] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const refresh = useCallback(() => {
    fetchMatches()
      .then(setMatches)
      .finally(() => setLoaded(true))
    // Fixtures come from the leagues, not from demos — this is the only thing that looks forwards.
    // Two sources: ESEA via FACEIT (prefetch opens the opponent's folder and warms their veto sheet
    // while we're here) and Nuke-liiga via esplay. They merge into one list because "what do we
    // play next" is one question; a coach should not have to check two places for it.
    //
    // Settled independently: esplay is a catalog of a single league and FACEIT is the whole of the
    // rest, so one being empty or erroring must not blank the other.
    Promise.allSettled([fetchUpcoming(true), fetchEsplayFixtures()])
      .then(([a, b]) => {
        const rows = [
          ...(a.status === 'fulfilled' ? a.value : []),
          ...(b.status === 'fulfilled' ? b.value : []),
        ]
        setNext(rows.sort((x, y) => x.scheduledAt - y.scheduledAt))
      })
      .catch(() => setNext([]))
      .finally(() => setNextLoaded(true))
  }, [])
  /** A walkover has no demo and never will, so the row has to stop asking for one. Optimistic:
   *  the mark is a click on a row the coach is looking at, and a round-trip to redraw it reads as
   *  the click not registering. If the write fails the row goes back to what the server believes. */
  const markForfeit = async (matchId: string, result: ForfeitResult | null) => {
    const before = next
    setNext((cur) => cur.map((m) => (m.matchId === matchId ? { ...m, forfeit: result ?? undefined } : m)))
    if (!(await setFixtureForfeit(matchId, result))) setNext(before)
  }

  useEffect(() => {
    refresh()
  }, [refresh])
  useEffect(() => {
    if (up.s !== 'uploading' && up.s !== 'queued') return
    const t = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(t)
  }, [up.s])

  const onFile = async (file: File) => {
    if (!/\.dem(\.zst|\.gz)?$/.test(file.name)) {
      setUp({ s: 'error', msg: 'Pick a .dem, .dem.gz or .dem.zst file' })
      return
    }
    try {
      setElapsed(0)
      setUp({ s: 'uploading', pct: 0 })
      const { demoId } = await uploadDemo(file, (pct) => setUp({ s: 'uploading', pct }))
      setUp({ s: 'queued', demoId })
      // Poll until the artifacts appear — but a crashed parse looks exactly like a slow one from
      // here, so ask status.json why rather than silently running out the clock.
      const started = Date.now()
      const poll = setInterval(async () => {
        const list = await fetchMatches()
        setMatches(list)
        if (list.some((m) => m.id === demoId)) {
          clearInterval(poll)
          setUp({ s: 'idle' })
          // File it into any opponent folder it involves — a league match lands in both teams'
          // folders from this single upload.
          autoAssignMatch(demoId, true).catch(() => {})
          return
        }
        const st = await fetchParseStatus(demoId).catch(() => null)
        if (st?.state === 'FAILED') {
          clearInterval(poll)
          setUp({ s: 'error', msg: st.error ? `Parse failed — ${st.error}` : 'Parse failed.' })
        } else if (Date.now() - started > 12 * 60_000) {
          clearInterval(poll)
          setUp({ s: 'error', msg: 'Still parsing after 12 min — check back shortly.' })
        }
      }, 8000)
    } catch (e) {
      setUp({ s: 'error', msg: (e as Error).message })
    }
  }

  return (
    <div className="space-y-5">
      {/* Upload */}
      <section
        className="border border-border bg-surface p-6"
        style={{ borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' }}
      >
        <SectionLabel n="00" label="Upload a demo" />
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0])
          }}
          onClick={() => inputRef.current?.click()}
          className="mt-4 flex cursor-pointer flex-col items-center justify-center border border-dashed border-border-bright py-10 text-center hover:bg-surface-2"
          style={{ borderRadius: 'var(--radius)' }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".dem,.gz,.zst"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
          {up.s === 'uploading' ? (
            <div className="w-64">
              <div className="text-sm text-text-dim">Uploading… {up.pct}%</div>
              <div className="mt-2 h-1.5 w-full overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                <div className="h-full bg-accent" style={{ width: `${up.pct}%` }} />
              </div>
            </div>
          ) : up.s === 'queued' ? (
            <div className="text-center">
              <div className="text-sm text-accent">Uploaded — parsing… {elapsed}s</div>
              <div className="mt-1 text-xs text-text-faint">a full match takes ~1–2 min · it appears below when done</div>
            </div>
          ) : (
            <>
              <div className="font-display text-sm text-text">Drop a .dem / .dem.zst here</div>
              <div className="mt-1 text-xs text-text-faint">or click to choose · direct-to-blob upload</div>
            </>
          )}
        </div>
        {up.s === 'error' && <p className="mt-2 text-xs text-loss">{up.msg}</p>}
      </section>

      {/* Fixtures — what we have not played yet. Nothing else in the portal looks forwards.
          While they are still loading the section is drawn with placeholder rows rather than left
          out: it sits ABOVE the match list, so appearing late pushed the list down under whoever
          was already reading it. Holding the space costs an empty box for a moment and keeps the
          thing people came for still. */}
      {!nextLoaded ? (
        <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
          <SectionLabel n="01" label="Next up" />
          <div className="mt-3 divide-y divide-border" aria-busy="true" aria-label="Loading fixtures">
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <span className="h-3 w-36 shrink-0 animate-pulse rounded bg-border" />
                <span className="h-3 w-40 animate-pulse rounded bg-border" />
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {nextLoaded && upcoming.length > 0 && (
        <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <SectionLabel n="01" label="Next up" />
            {canCoach && (
              <button
                onClick={() => setShowBrief((v) => !v)}
                className="text-xs text-text-dim hover:text-accent"
                title="What gets posted to Discord at 18:00 on match day"
              >
                {showBrief ? 'hide brief' : 'pre-match brief'}
              </button>
            )}
          </div>
          {showBrief && <BriefPreview />}
          <div className="mt-3 divide-y divide-border">
            {upcoming.map((m) => (
              <FixtureRow key={m.matchId} m={m} canCoach={canCoach} onForfeit={markForfeit} onOpponent={onOpponent} />
            ))}
          </div>
        </section>
      )}

      {/* What we don't yet hold on the teams we play next. Sits under "Next up" because it is the
          work that fixture creates, and it grows through a season: an opponent has played twice in
          week one and ten times by the playoffs, and nothing used to say which of those you had. */}
      {nextLoaded && upcoming.length > 0 && <ScoutingGaps canCoach={canCoach} onDone={refresh} />}

      {/* Behind us, and still owing something: a demo nobody has uploaded, or a walkover whose mark
          can still be undone. These used to sit under "Next up" wearing a badge to explain why they
          were there — but a match that has happened is not a fixture however it is badged, and a
          heading that means "prepare for this" was the wrong place to keep asking for a demo. */}
      {nextLoaded && settled.length > 0 && (
        <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
          <SectionLabel n="02" label="Loose ends" />
          <div className="mt-3 divide-y divide-border">
            {settled.map((m) => (
              <FixtureRow key={m.matchId} m={m} canCoach={canCoach} onForfeit={markForfeit} onOpponent={onOpponent} />
            ))}
          </div>
        </section>
      )}

      {/* Match list */}
      <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel n="03" label="Matches" />
          <div className="flex flex-wrap items-center gap-2">
            {maps.length > 1 && (
              <select
                aria-label="Filter by map"
                value={fMap}
                onChange={(e) => setFMap(e.target.value)}
                className="border border-border bg-bg-2 px-2 py-1 text-xs text-text-dim outline-none"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <option value="">All maps</option>
                {maps.map((m) => (
                  <option key={m} value={m}>
                    {m.replace('de_', '')}
                  </option>
                ))}
              </select>
            )}
            {leagues.length > 1 && (
              <select
                aria-label="Filter by competition"
                value={fLeague}
                onChange={(e) => setFLeague(e.target.value)}
                className="max-w-52 border border-border bg-bg-2 px-2 py-1 text-xs text-text-dim outline-none"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <option value="">All competitions</option>
                {leagues.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            )}
            <input
              aria-label="Filter by opponent"
              value={fOpp}
              onChange={(e) => setFOpp(e.target.value)}
              placeholder="opponent…"
              className="w-28 border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            />
            {(fMap || fLeague || fOpp) && (
              <button
                onClick={() => {
                  setFMap('')
                  setFLeague('')
                  setFOpp('')
                }}
                className="text-xs text-text-faint hover:text-accent"
              >
                clear
              </button>
            )}
            <button onClick={refresh} className="text-xs text-text-dim hover:text-accent">
              refresh
            </button>
          </div>
        </div>
        <div className="mt-4 divide-y divide-border">
          {shown.map((m) => {
            // Resolve OUR side by name, never by position. This used to take Object.entries order
            // and paint entry[0]'s score in the accent colour that means "us" everywhere else —
            // so on 3 of 5 rows it highlighted the opponent, and a 2-13 loss read as a 13-2 win.
            const entries = Object.entries(m.teamScore)
            const us = entries.find(([name]) => /jano/i.test(name)) ?? entries[0]
            const them = entries.find((e) => e !== us) ?? entries[1]
            return (
              <div key={m.id} className="flex items-center gap-1">
                <button
                  onClick={() => onSelect(m.id)}
                  className="flex flex-1 items-center gap-4 py-3 text-left hover:bg-surface-2"
                >
                  <span className="font-display w-24 text-sm uppercase tracking-wider text-text-dim">{m.map}</span>
                  <span className="font-display tabular-nums text-text">
                    {us?.[0]} <span className="text-accent">{us?.[1]}</span> – {them?.[1]} {them?.[0]}
                  </span>
                  {m.league && (
                    <span
                      title={m.league}
                      className="max-w-56 truncate rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                      style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
                    >
                      {m.league}
                    </span>
                  )}
                  <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-text-faint">
                    {m.finishedAt ? <span>{fmtDate(m.finishedAt)}</span> : null}
                    <span>{m.sample ? 'sample' : `${m.rounds} rounds`}</span>
                  </span>
                </button>
                {!m.sample && canCoach && (
                  <button
                    title="Delete match"
                    onClick={async () => {
                      if (!confirm(`Delete this ${m.map} match? This removes the demo and its stats.`)) return
                      await deleteMatch(m.id).catch(() => {})
                      refresh()
                    }}
                    className="px-2 py-1 text-text-faint hover:text-loss"
                  >
                    ✕
                  </button>
                )}
              </div>
            )
          })}
          {!shown.length && (
            <div className="py-4 text-sm text-text-faint">
              {!loaded
                ? 'Loading…'
                : matches.length
                  ? 'No match fits those filters.'
                  : 'No matches yet — drop a .dem above and it parses itself in a minute or two.'}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

/** What the pre-match message will say, before the team reads it.
 *
 *  The brief posts itself about three hours out, which is the point — nobody opens a portal on the
 *  way to a match. But a message that writes itself is one nobody has checked, so a coach can see
 *  the wording here, and send it early if they want it now. */
/** One fixture row, shared by "Next up" and "Loose ends". The two lists differ in which fixtures
 *  they hold, not in how a fixture reads — the row still says whether it is ahead of us, played, or
 *  no game at all, because it is also what a coach clicks to change that. */
function FixtureRow({
  m,
  canCoach,
  onForfeit,
  onOpponent,
}: {
  m: NextMatch
  canCoach: boolean
  onForfeit: (matchId: string, result: ForfeitResult | null) => void
  onOpponent?: (id: string) => void
}) {
  const when = m.scheduledAt ? new Date(m.scheduledAt * 1000) : null
  // Ahead of us and close. The future half matters: a match played last night is also "within 36
  // hours", and colouring it like tonight's game made a settled walkover read as the next thing on.
  const soon = when ? when.getTime() > Date.now() && when.getTime() - Date.now() < 36 * 3600_000 : false
  // Played but no demo — the to-do this row exists for. A forfeit is played-and-settled: there was
  // no game, so no demo is outstanding. Neither is one ever outstanding for an esplay fixture:
  // fetching a Nuke-liiga demo needs a signed-in esplay session, so "demo missing" there would be a
  // permanent red badge for something nobody can do anything about. This also keeps the ffw/ffl
  // controls off those rows — they post to a FACEIT-only endpoint.
  const owed = m.source !== 'esplay' && m.played && !m.haveDemo && !m.forfeit
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 text-sm">
      <span
        className="font-display w-36 shrink-0 uppercase tracking-wider"
        style={{ color: owed ? 'var(--loss)' : soon ? 'var(--accent)' : 'var(--text-dim)' }}
      >
        {m.scheduledAt ? fmtFixture(m.scheduledAt) : 'TBD'}
      </span>
      <span className="text-text-faint">{m.forfeit ? 'no game' : m.played ? 'played' : 'vs'}</span>
      <span className="font-display text-text">{m.opponent}</span>
      {/* A played esplay fixture carries its own result: there is no demo to read it from, so the
          row is the only place the score appears. Maps won, ours first. */}
      {m.played && m.series && (
        <span
          className="font-display"
          style={{ color: m.series[0] > m.series[1] ? 'var(--accent)' : 'var(--text-dim)' }}
          title={`${m.series[0]}–${m.series[1]} on maps`}
        >
          {m.series[0]}–{m.series[1]}
        </span>
      )}
      {!m.played && !!m.bestOf && (
        <span className="text-[11px] text-text-faint" title="Maps in this fixture">
          bo{m.bestOf}
        </span>
      )}
      {owed && (
        <span
          className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
          style={{ background: 'var(--loss)', color: '#fff' }}
          title="This match is played but its demo hasn't been uploaded"
        >
          demo missing
        </span>
      )}
      {m.forfeit && (
        <span
          className="rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
          style={{
            background: 'var(--border)',
            color: m.forfeit === 'ffw' ? 'var(--accent)' : 'var(--loss)',
          }}
          title={
            m.forfeit === 'ffw'
              ? 'Forfeit win — they never joined, so there is no demo to upload'
              : 'Forfeit loss — no game was played, so there is no demo to upload'
          }
        >
          {m.forfeit}
        </span>
      )}
      {m.played && m.haveDemo && <span className="text-[11px] text-text-faint">demo in</span>}
      <span
        className="max-w-56 truncate rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
        style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
        title={m.competition}
      >
        {m.competition}
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-3">
        {canCoach && m.forfeit && (
          <button
            onClick={() => onForfeit(m.matchId, null)}
            className="text-[11px] text-text-faint hover:text-accent"
            aria-label={`Undo the forfeit mark on ${m.opponent} and expect a demo again`}
            title="It was played after all — put the demo back on the to-do list"
          >
            undo ff
          </button>
        )}
        {canCoach && owed && (
          <span className="flex items-center gap-2 text-[11px] text-text-faint">
            <span className="text-text-faint">no demo?</span>
            <button
              onClick={() => onForfeit(m.matchId, 'ffw')}
              className="hover:text-accent"
              aria-label={`Mark ${m.opponent} as a forfeit win — no game, no demo`}
              title="We won by forfeit — nobody turned up"
            >
              ffw
            </button>
            <button
              onClick={() => onForfeit(m.matchId, 'ffl')}
              className="hover:text-loss"
              aria-label={`Mark ${m.opponent} as a forfeit loss — no game, no demo`}
              title="We forfeited — no game was played"
            >
              ffl
            </button>
          </span>
        )}
        {m.vetoReady && <span className="text-[11px] text-text-faint">veto ready</span>}
        {m.opponentId && onOpponent && (
          <button onClick={() => onOpponent(m.opponentId!)} className="text-xs text-accent hover:underline">
            scout them →
          </button>
        )}
      </span>
    </div>
  )
}

function BriefPreview() {
  const [state, setState] = useState<{ briefs: PreMatchBrief[]; discord: boolean } | null>(null)
  const [sent, setSent] = useState<string[]>([])

  useEffect(() => {
    fetchBriefs()
      .then(setState)
      .catch(() => setState({ briefs: [], discord: false }))
  }, [])

  if (!state) return <p className="mt-3 text-xs text-text-dim">Building…</p>
  if (!state.briefs.length) {
    return (
      <p className="mt-3 text-xs text-text-faint">
        Nothing to brief yet — there's no fixture with anything known about the opponent.
      </p>
    )
  }
  return (
    <div className="mt-3 space-y-3">
      {!state.discord && (
        <p className="text-xs text-loss">
          No Discord webhook configured on the API, so this won't post itself — it's a preview only.
        </p>
      )}
      {state.briefs.map((b) => (
        <div key={b.matchId} className="border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
          <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-text-dim">{b.text}</pre>
          <div className="mt-2 flex items-center gap-3">
            <button
              onClick={async () => {
                if (await sendBrief(b.matchId)) setSent((s) => [...s, b.matchId])
              }}
              disabled={!state.discord || sent.includes(b.matchId)}
              className="text-[11px] text-accent hover:underline disabled:opacity-40"
            >
              {sent.includes(b.matchId) ? 'sent' : 'send now'}
            </button>
            <span className="text-[10px] text-text-faint">
              otherwise it posts itself at 18:00 on match day (09:00 for a daytime fixture)
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

/** The demos we don't hold on the teams we play next — and the quickest way to get them.
 *
 *  Downloading a FACEIT demo needs the Downloads API scope, which is an applied-for token the
 *  deployment does not have, so the fetch itself stays manual: a signed-in human opens the match
 *  room and downloads. What this removes is the two parts that actually scale badly — REMEMBERING
 *  which of an opponent's games you are missing, and moving ~300 MB down to a laptop and back up.
 *  Paste the link the browser was given and the parser fetches it directly.
 *
 *  Everything here runs on the Data API key, which works today. If the Downloads token is ever
 *  granted, the same list simply stops having rows in it.
 */
function ScoutingGaps({ canCoach, onDone }: { canCoach: boolean; onDone: () => void }) {
  const [gaps, setGaps] = useState<FixtureGap[] | null>(null)
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    fetchDemoGaps()
      .then(setGaps)
      .catch(() => setGaps([]))
  }, [])

  if (!gaps) return null
  // Only the fixtures that are actually short of something. A fully-scouted opponent is not news,
  // and a panel that is always there stops being read.
  const short = gaps.filter((g) => g.demos.some((d) => d.published && !d.held))
  if (!short.length) return null

  const submit = async () => {
    const v = url.trim()
    if (!v) return
    setBusy(true)
    setNote(null)
    try {
      await addDemoFromUrl(v, 'scouting')
      setUrl('')
      setNote('Queued — it parses itself and lands in their folder in a minute or two.')
      onDone()
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
      <SectionLabel n="01b" label="Scouting gaps" />
      <div className="mt-3 space-y-4">
        {short.map((g) => (
          <div key={g.matchId}>
            <div className="flex flex-wrap items-baseline gap-2 text-sm">
              <span className="font-display text-text">{g.opponent}</span>
              <span className="text-text-faint">
                {g.scheduledAt ? fmtFixture(g.scheduledAt) : 'TBD'} · you hold {g.held} of {g.available}
              </span>
              {g.opponentId && (
                <a href={`#/scouting/${g.opponentId}`} className="text-xs text-accent hover:underline">
                  their folder →
                </a>
              )}
            </div>
            <div className="mt-1 divide-y divide-border">
              {g.demos.map((d) => (
                <div key={d.matchId} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                  <span className="w-28 shrink-0 text-text-faint">
                    {d.scheduledAt ? fmtDate(d.scheduledAt) : '—'}
                  </span>
                  <span className="text-text-dim">{d.teams.join(' vs ')}</span>
                  {d.maps.length > 0 && (
                    <span className="text-text-faint">{d.maps.map((m) => m.replace('de_', '')).join(', ')}</span>
                  )}
                  {/* Which tournament it was. These rows used to be one competition by
                      construction; now a Nuke-liiga opponent's ESEA games are listed too, and
                      "same team, other league" is the difference between a useful demo and a
                      confusing one. */}
                  {d.competition && (
                    <span
                      className="max-w-44 truncate rounded px-1.5 py-px text-[10px] uppercase tracking-wider"
                      style={{ background: 'var(--border)', color: 'var(--text-dim)' }}
                      title={d.competition}
                    >
                      {d.competition}
                    </span>
                  )}
                  <span className="ml-auto flex shrink-0 items-center gap-3">
                    {d.held ? (
                      <span className="text-win">have it</span>
                    ) : !d.published ? (
                      // Not a to-do: FACEIT never published a demo for this one, so there is
                      // nothing to fetch by any route. Saying so stops it reading as a task.
                      <span className="text-text-faint" title="FACEIT has not published a demo for this match">
                        no demo published
                      </span>
                    ) : (
                      <a
                        href={d.roomUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline"
                        title="Open the match room, download the demo, then paste its link below"
                      >
                        get it →
                      </a>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {canCoach && (
          <div className="border-t border-border pt-3">
            <label htmlFor="demo-link" className="text-xs text-text-dim">
              Paste a demo link
            </label>
            <p className="mt-0.5 text-[11px] text-text-faint">
              In the match room, right-click the download button → copy link address. The parser
              fetches it directly, so nothing downloads through this browser.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                id="demo-link"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                placeholder="https://…faceit-cdn.net/cs2/….dem.zst?…"
                className="min-w-64 flex-1 border border-border bg-bg-2 px-3 py-2 text-xs text-text outline-none focus:border-accent"
                style={{ borderRadius: 'var(--radius)' }}
              />
              <button
                onClick={submit}
                disabled={busy || !url.trim()}
                className="border border-border px-3 py-2 text-xs text-text-dim hover:text-accent disabled:opacity-40"
                style={{ borderRadius: 'var(--radius)' }}
              >
                {busy ? 'queueing…' : 'add'}
              </button>
            </div>
            {note && (
              <p role="alert" className="mt-2 text-[11px] text-text-dim">
                {note}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
